import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { browserHistory } from 'react-router';

import {
  EModule,
  FallbackAppBarLinks,
  FallbackButtonRenderLinks,
  FederatedModule,
  IAppBarLinksProps,
  IButtonRenderLinksProps,
} from '@lumahealthhq/ui-common';
import { AppBar, createStyles, IconButton, Theme, Toolbar, WithStyles, withStyles } from '@material-ui/core';
import { AppBarProps } from '@material-ui/core/AppBar';
import { Settings as SettingsIcon } from '@material-ui/icons';
import classnames from 'classnames';
import { LanguageSelector } from 'core';
import { getTheme } from 'core/styles/getTheme';
import { get } from 'lodash';

import {
  selectCurrentMaster,
  selectCurrentMasterSettings,
  selectCurrentUser,
  selectCurrentUserSettings,
  selectCustomThemeProps,
} from '@lib/selectors';

import { useTelehealthSessionContext } from 'screens/TelehealthScreen/hooks';

import messages from './messages';
import { useHeaderViewModel } from './useHeaderViewModel';

const styles = ({ breakpoints: { down } }: Theme) =>
  createStyles({
    root: {
      // NOTE: This class is being overridden from MUI theme props
    },
    logo: {
      // NOTE: This class is being overridden from MUI theme props
    },
    grow: {
      flexGrow: 1,
      zIndex: 1,
    },
    title: {
      position: 'absolute',
      textAlign: 'center',
      left: 0,
      right: 0,
      [down('md')]: {
        display: 'none',
      },
    },
    welcome: {
      fontSize: 18,
    },
    settingsIcon: {
      paddingRight: 0,
      '&:hover': { 'background-color': 'transparent' },
    },
    // nexty-patient-forms
    rootNextyPatientForms: {
      // WORKAROUND This shadow will be defined by the theme
      // once we move the header into a federated module
      boxShadow:
        '0px 0px 1px 0px rgba(32, 35, 39, 0.16), 0px 4px 8px 0px rgba(32, 35, 39, 0.04), 0px 8px 48px 0px rgba(32, 35, 39, 0.02)',
    },
    growNextyPatientForms: {
      display: 'flex',
    },
  });

export interface HeaderProps extends WithStyles<typeof styles>, Omit<AppBarProps, 'classes'> {
  logoSrc?: string;
  logo?: React.ReactNode;
  includeActions?: boolean;
  secondaryAction?: React.ReactNode;
  handleLumaBotClose?: () => void;
  toolbarProps?: any;
  includeLanguageSelector?: boolean;
  includeBarWithLinks?: boolean;
  includeButtonWithLinks?: boolean;
  links?: IButtonRenderLinksProps['links'];
}

const Header: React.FC<HeaderProps> = ({
  classes,
  className,
  logo,
  includeActions = false,
  secondaryAction,
  logoSrc,
  handleLumaBotClose,
  toolbarProps,
  includeLanguageSelector,
  includeBarWithLinks,
  includeButtonWithLinks,
  links = [],
  ...rest
}) => {
  const { isGuestCredentialsRoute, guestName } = useTelehealthSessionContext();
  const isGuestLink = isGuestCredentialsRoute || !!guestName;
  const { rootAccount } = useHeaderViewModel(isGuestLink);
  const currentMaster = useSelector(selectCurrentMaster);

  const masterUser = rootAccount || currentMaster; // <-- if we cant get root account, use current master from selector

  const currentUser = useSelector(selectCurrentUser);
  const currentMasterSettings = useSelector(selectCurrentMasterSettings);
  const currentUserSettings = useSelector(selectCurrentUserSettings);
  const isWidgetUser = currentUser ? currentUser.roles.includes('widget') : false;
  const isRandomNamed = currentUser ? get(currentUser, 'randomName') : false;
  const themeProps = useSelector(selectCustomThemeProps);
  const theme = getTheme(themeProps);

  const hidePatientName = get(
    currentMasterSettings,
    'patientForms.hidePatientName',
    get(currentUserSettings, 'patientForms.hidePatientName', false),
  );
  const hidePatientNameOnForms = hidePatientName && /survey/.test(window.location.pathname);
  const showPatientName = !isWidgetUser && !isRandomNamed && !hidePatientNameOnForms;

  const hasCampaignsEnabled = get(currentMaster, 'settings.communication.campaigns.enabled', false);
  const isMainScreen = /main/.test(window.location.pathname);
  const includeSettingsButton = hasCampaignsEnabled && isMainScreen;

  return (
    <AppBar
      className={classnames(classes.root, className, classes.rootNextyPatientForms)}
      position="fixed"
      color="inherit"
      {...rest}
    >
      {includeBarWithLinks && links.length > 0 && (
        <FederatedModule<IAppBarLinksProps>
          module={EModule.AppBarLinks}
          fallback={<FallbackAppBarLinks />}
          props={{
            sx: { '& > .MuiToolbar-root': { minHeight: 35 } },
            position: 'relative',
            links,
          }}
        />
      )}
      <Toolbar {...toolbarProps}>
        <div className={classnames(classes.grow, classes.growNextyPatientForms)}>
          {logo || (
            <img
              className={classes.logo}
              src={
                get(theme, 'overrides.LumaHeader.logo.content') ||
                get(theme, 'props.LumaHeader.logoSrc') ||
                logoSrc ||
                ''
              }
              alt="Logo"
            />
          )}
        </div>
        {masterUser ? (
          <div className={classes.title}>
            <strong>{masterUser.name}</strong>
            <br />
            {showPatientName && (
              <FormattedMessage {...messages.welcome} values={{ user: get(currentUser, 'name') }}>
                {(text) => <span className={classes.welcome}>{text}</span>}
              </FormattedMessage>
            )}
          </div>
        ) : null}
        {includeLanguageSelector && <LanguageSelector />}
        {includeButtonWithLinks && links.length > 0 && (
          <FederatedModule<IButtonRenderLinksProps>
            module={EModule.ButtonRenderLinks}
            fallback={<FallbackButtonRenderLinks />}
            props={{
              sx: { '& > .MuiSvgIcon-root': { width: 32, height: 32 } },
              color: 'primary',
              links,
            }}
          />
        )}
        {includeSettingsButton && (
          <IconButton className={classes.settingsIcon} onClick={() => browserHistory.push('/settings')}>
            <SettingsIcon fontSize="large" />
          </IconButton>
        )}
        {secondaryAction}
      </Toolbar>
    </AppBar>
  );
};

export default withStyles(styles, { name: 'LumaHeader' })(Header);
